/**
 * Onboarding Context Utility
 * 
 * Utility functions to detect and handle invitation context in URLs
 * Used to determine if user is in invitation flow vs direct access
 */

/**
 * Check if URL has invitation context parameters
 * @param searchParams - URL search parameters
 * @returns True if invitation context exists
 */
export const hasInvitationContext = (searchParams: URLSearchParams | null): boolean => {
  if (!searchParams) return false
  
  return !!(
    searchParams.get('token') ||
    searchParams.get('tempToken') ||
    searchParams.get('otpVerified') === 'true' ||
    searchParams.get('onboardingToken') ||
    searchParams.get('verifiedToken')
  )
}

/**
 * Extract all invitation-related parameters from URL
 * @param searchParams - URL search parameters
 * @returns Object with invitation parameters
 */
export const getInvitationParams = (
  searchParams: URLSearchParams | null
): Record<string, string> => {
  if (!searchParams) return {}
  
  const params: Record<string, string> = {}
  const invitationKeys = ['token', 'tempToken', 'onboardingToken', 'verifiedToken']
  
  invitationKeys.forEach(key => {
    const value = searchParams.get(key)
    if (value) params[key] = value
  })
  
  // Add otpVerified if true
  if (searchParams.get('otpVerified') === 'true') {
    params.otpVerified = 'true'
  }
  
  return params
}

/**
 * Preserve invitation parameters when navigating
 * @param baseUrl - Base URL to navigate to
 * @param currentSearchParams - Current URL search parameters
 * @param additionalParams - Additional parameters to add
 * @returns URL with preserved invitation parameters
 */
export const preserveInvitationParams = (
  baseUrl: string,
  currentSearchParams: URLSearchParams | null,
  additionalParams: Record<string, string> = {}
): string => {
  const params = new URLSearchParams()
  
  // Preserve existing invitation params
  const existingParams = getInvitationParams(currentSearchParams)
  Object.entries(existingParams).forEach(([key, value]) => {
    params.set(key, value)
  })
  
  // Add additional params (will override existing if same key)
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      params.set(key, String(value))
    }
  })
  
  const queryString = params.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

/**
 * Check if current route is an onboarding route
 * @param pathname - Current pathname
 * @returns True if pathname contains onboarding
 */
export const isOnboardingRoute = (pathname: string): boolean => {
  return pathname.includes('/onboarding/')
}

